import { Download, X, Check } from "lucide-react";
import { usePwaInstall } from "@/lib/usePwaInstall";

/**
 * Install button — three rendering variants via the `variant` prop:
 *   - "nav"  : slim strip pinned just above the mobile bottom-nav
 *   - "menu" : full-width button inside the hamburger drawer
 *   - "pill" : floating pill (desktop / tablet)
 *
 * Hidden entirely when the app is already running standalone.
 * iOS Safari users see the "Add to Home Screen" walkthrough via the
 * global IOSInstallModal triggered by the `luxsova:show-ios-install` event.
 */
const InstallAppButton = ({ variant = "menu", onAfterClick }) => {
  const {
    canInstall,
    isIOS,
    isStandalone,
    dismissedRecently,
    promptInstall,
    markDismissed,
  } = usePwaInstall();

  if (isStandalone) {
    if (variant === "menu") {
      return (
        <div
          className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-emerald-700 border border-emerald-200 bg-emerald-50 px-4 py-3"
          data-testid="app-installed-indicator"
        >
          <Check className="w-4 h-4" /> App installed
        </div>
      );
    }
    return null;
  }

  // Hide entirely when neither native install nor iOS is available.
  if (!canInstall && !isIOS) return null;

  // Don't nag with the slim nav strip if user dismissed an iOS hint recently.
  if (isIOS && !canInstall && dismissedRecently && variant === "nav") return null;

  const handleClick = async () => {
    if (canInstall) {
      const { outcome } = await promptInstall();
      if (outcome === "dismissed") markDismissed();
    } else if (isIOS) {
      window.dispatchEvent(new CustomEvent("luxsova:show-ios-install"));
    }
    onAfterClick?.();
  };

  if (variant === "nav") {
    return (
      <div
        className="md:hidden fixed left-0 right-0 z-[68] flex items-stretch bg-black text-white border-t border-white/10"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 60px)" }}
      >
        <button
          onClick={handleClick}
          className="flex-1 flex items-center justify-center gap-2 text-[12px] uppercase tracking-[0.18em] py-2.5"
          data-testid="install-app-nav"
        >
          <Download className="w-4 h-4" />
          Download app
        </button>
        <button
          type="button"
          onClick={markDismissed}
          className="px-3 opacity-70 hover:opacity-100"
          aria-label="Dismiss install prompt"
          data-testid="install-app-nav-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  if (variant === "pill") {
    return (
      <button
        onClick={handleClick}
        className="inline-flex items-center gap-2 bg-black text-white text-xs uppercase tracking-[0.18em] px-4 py-2.5 hover:bg-neutral-800 transition-colors"
        data-testid="install-app-pill"
      >
        <Download className="w-4 h-4" />
        Download app
      </button>
    );
  }

  // menu variant
  return (
    <button
      onClick={handleClick}
      className="w-full inline-flex items-center justify-center gap-2 bg-black text-white text-[12px] uppercase tracking-[0.18em] py-3 px-4 hover:bg-neutral-800 transition-colors"
      data-testid="install-app-menu"
    >
      <Download className="w-4 h-4" />
      Download app
    </button>
  );
};

export default InstallAppButton;
